import React, { useState } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { Phone, Repeat, CheckCircle, XCircle, Flag, Send } from 'lucide-react';

const S = {
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-sm)',
  },
  row: {
    display: 'flex',
    gap: 'var(--space-sm)',
    flexWrap: 'wrap',
  },
  divider: {
    height: '1px',
    backgroundColor: 'rgba(212, 175, 55, 0.1)',
    margin: 'var(--space-sm) 0',
  },
  hint: {
    fontSize: '0.8rem',
    color: 'var(--color-text-muted)',
    margin: 0,
    fontFamily: 'var(--font-body)',
  },
  offerPendingText: {
    fontSize: '0.9rem',
    color: 'var(--color-warning)',
    margin: '0 0 var(--space-sm)',
    fontFamily: 'var(--font-display)',
    letterSpacing: '0.03em',
  },
  offerForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-sm)',
    padding: 'var(--space-sm)',
    backgroundColor: 'rgba(243, 156, 18, 0.06)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(243, 156, 18, 0.15)',
  },
  offerFormLabel: {
    fontFamily: 'var(--font-display)',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'var(--color-warning)',
    letterSpacing: '0.05em',
    margin: 0,
  },
  offerInputRow: {
    display: 'flex',
    gap: 'var(--space-sm)',
    alignItems: 'flex-end',
  },
  swapSelect: {
    width: '100%',
    padding: '8px 10px',
    backgroundColor: 'var(--color-bg-primary)',
    border: '1px solid rgba(212, 175, 55, 0.15)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-body)',
    fontSize: '0.85rem',
  },
};

export default function ActionButtons({
  game,
  onMakeOffer,
  onRespondOffer,
  onCompleteGame,
  loading,
}) {
  const [offerMode, setOfferMode] = useState(null); // null | 'money' | 'swap'
  const [offerAmount, setOfferAmount] = useState('');
  const [swapBoxNumber, setSwapBoxNumber] = useState('');

  if (!game) return null;

  const isSetup = game.status === 'setup';
  const isPlaying = game.status === 'playing';
  const isOfferPending = game.status === 'offer_pending';
  const isCompleted = game.status === 'completed' || game.status === 'cancelled';

  if (isCompleted) return null;

  // Available boxes for swap (unopened, not player's box)
  const swappableBoxes = (game.boxes || []).filter(
    (b) => !b.is_opened && !b.is_player_box
  );

  const handleSubmitMoneyOffer = () => {
    const amount = parseFloat(offerAmount);
    if (isNaN(amount) || amount < 0) return;
    onMakeOffer?.({ amount });
    setOfferMode(null);
    setOfferAmount('');
  };

  const handleSubmitSwapOffer = () => {
    const boxNum = parseInt(swapBoxNumber, 10);
    if (isNaN(boxNum)) return;
    onMakeOffer?.({ swap_box_number: boxNum });
    setOfferMode(null);
    setSwapBoxNumber('');
  };

  const handleCancel = () => {
    setOfferMode(null);
    setOfferAmount('');
    setSwapBoxNumber('');
  };

  const lastOffer = game.last_offer;
  const isSwapOffer = isOfferPending && lastOffer?.swap_box_number;

  return (
    <Card header="Azioni">
      <div style={S.group}>
        {isSetup && (
          <p style={S.hint}>
            {game.player_box_number
              ? 'La scatola del giocatore e\' stata selezionata. La partita iniziera\' al primo turno.'
              : 'Seleziona una scatola dalla griglia per assegnarla al giocatore.'}
          </p>
        )}

        {isPlaying && !offerMode && (
          <>
            <p style={S.hint}>
              Apri scatole dalla griglia, oppure chiama il Banchiere.
            </p>
            <div style={S.row}>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setOfferMode('money')}
              >
                <Phone size={16} />
                Offerta in denaro
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setOfferMode('swap')}
                disabled={swappableBoxes.length === 0}
              >
                <Repeat size={16} />
                Proponi scambio
              </Button>
            </div>
          </>
        )}

        {isPlaying && offerMode === 'money' && (
          <div style={S.offerForm}>
            <p style={S.offerFormLabel}>Offerta del Banchiere</p>
            <div style={S.offerInputRow}>
              <div style={{ flex: 1 }}>
                <Input
                  type="number"
                  value={offerAmount}
                  onChange={(e) => setOfferAmount(e.target.value)}
                  placeholder="Importo in euro"
                  min="0"
                  step="any"
                  autoFocus
                />
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSubmitMoneyOffer}
                loading={loading}
                disabled={!offerAmount || parseFloat(offerAmount) < 0}
              >
                <Send size={14} />
                Invia
              </Button>
            </div>
            <Button variant="ghost" size="sm" onClick={handleCancel} style={{ alignSelf: 'flex-start' }}>
              Annulla
            </Button>
          </div>
        )}

        {isPlaying && offerMode === 'swap' && (
          <div style={S.offerForm}>
            <p style={S.offerFormLabel}>Scambio scatola</p>
            <p style={{ ...S.hint, marginBottom: '4px' }}>
              Seleziona la scatola da proporre in scambio al giocatore.
            </p>
            <div style={S.offerInputRow}>
              <select
                style={S.swapSelect}
                value={swapBoxNumber}
                onChange={(e) => setSwapBoxNumber(e.target.value)}
              >
                <option value="">Scegli scatola...</option>
                {swappableBoxes.map((box) => (
                  <option key={box.box_number} value={box.box_number}>
                    Scatola {box.box_number}{box.region_name ? ` — ${box.region_name}` : ''}
                  </option>
                ))}
              </select>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSubmitSwapOffer}
                loading={loading}
                disabled={!swapBoxNumber}
              >
                <Send size={14} />
                Invia
              </Button>
            </div>
            <Button variant="ghost" size="sm" onClick={handleCancel} style={{ alignSelf: 'flex-start' }}>
              Annulla
            </Button>
          </div>
        )}

        {isOfferPending && (
          <>
            <p style={S.offerPendingText}>
              {isSwapOffer
                ? `Scambio proposto: scatola ${lastOffer.swap_box_number}`
                : 'Offerta in attesa di risposta'}
            </p>
            <div style={S.row}>
              <Button
                variant="primary"
                size="sm"
                onClick={() => onRespondOffer?.(true)}
                loading={loading}
              >
                <CheckCircle size={16} />
                Accetta
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => onRespondOffer?.(false)}
                loading={loading}
              >
                <XCircle size={16} />
                Rifiuta
              </Button>
            </div>
          </>
        )}

        {(isPlaying || isOfferPending) && (
          <>
            <div style={S.divider} />
            <Button
              variant="ghost"
              size="sm"
              onClick={onCompleteGame}
              loading={loading}
              style={{ alignSelf: 'flex-start' }}
            >
              <Flag size={14} />
              Termina partita
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}
